import { Injectable } from "@angular/core";
import { Observable, of, delay, tap } from "rxjs";
import { ProfileCompletionService } from "./profile.completion.service";
import {
  Profile,
  Experience,
  Education,
} from "../../interfaces/profile/profile.interface";
import { CommonSearchParam } from "../../../../core/interfaces/common-search-param.interface";
import { PageResponse } from "../../../../core/interfaces/page-response.interface";

@Injectable({
  providedIn: "root",
})
export class ProfileCompletionMockService extends ProfileCompletionService {
  private mockProfiles: Profile[] = [
    {
      gender: "Male",
      dateOfBirth: "1995-05-15",
      fullName: "John Doe",
      addressLine1: "123 Tech Lane",
      addressLine2: "Apt 4B",
      city: "Bangalore",
      state: "Karnataka",
      country: "India",
      pincode: "560001",
      headline: "Senior Software Engineer",
      education: [],
      experience: [],
      skills: ["Angular", "TypeScript"],
      bio: "I am a passionate software engineer with experience in building modern web applications.",
    },
  ];

  constructor() {
    super();
  }

  override findAll(
    searchParam: CommonSearchParam
  ): Observable<PageResponse<Profile>> {
    let filtered = [...this.mockProfiles];

    if (searchParam.searchTerm) {
      const term = searchParam.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.fullName.toLowerCase().includes(term) ||
          p.headline.toLowerCase().includes(term) ||
          p.city.toLowerCase().includes(term)
      );
    }

    const size = searchParam.size || 10;
    const page = searchParam.page || 0;

    const start = page * size;
    const end = start + size;

    const paginatedContent = filtered.slice(start, end);

    return this.wrapRequest(
      of({
        content: paginatedContent,
        page: {
          number: page,
          size: size,
          totalElements: filtered.length,
          totalPages: Math.ceil(filtered.length / size),
        },
      }).pipe(delay(1000)),
      false,
      "Failed to fetch profiles"
    );
  }

  override find(id: string): Observable<Profile> {
    const profile = this.mockProfiles[0];
    return this.wrapRequest(
      of(profile).pipe(delay(1000)),
      false,
      "Failed to fetch profile"
    );
  }

  override create(data: Partial<Profile>): Observable<Profile> {
    const newProfile = { ...data } as Profile;
    this.mockProfiles.push(newProfile);

    return this.wrapRequest(
      of(newProfile).pipe(delay(1500)),
      false,
      "Failed to create profile"
    );
  }

  override update(id: string, data: Partial<Profile>): Observable<Profile> {
    this.mockProfiles[0] = { ...this.mockProfiles[0], ...data } as Profile;
    return this.wrapRequest(
      of(this.mockProfiles[0]).pipe(delay(1500)),
      false,
      "Failed to update profile"
    );
  }

  override delete(id: string): Observable<void> {
    return this.wrapRequest(
      of(undefined).pipe(delay(1000)),
      false,
      "Failed to delete profile"
    );
  }

  override getProfileDetails(): Observable<Profile> {
    return this.wrapRequest(
      of(this.mockProfiles[0]).pipe(delay(1000)),
      false,
      "Failed to fetch profile details"
    );
  }

  override updateBasicInfo(data: Profile): Observable<Profile> {
    this.mockProfiles[0] = { ...data };
    return this.wrapRequest(
      of(this.mockProfiles[0]).pipe(delay(1500)),
      false,
      "Failed to update basic info"
    );
  }

  override updateUserType(userType: string): Observable<void> {
    return this.wrapRequest(
      of(undefined).pipe(delay(1000)),
      false,
      "Failed to update user type"
    );
  }

  override updateSkills(data: { skills: string[], careerObjective: string }): Observable<any> {
    this.mockProfiles[0].skills = data.skills;
    this.mockProfiles[0].bio = data.careerObjective;
    return this.wrapRequest(
      of({ success: true }).pipe(delay(1500)),
      false,
      "Failed to update skills"
    );
  }

  override uploadProfilePicture(
    uniqueId: string,
    file: File,
    isPrimary: boolean
  ): Observable<any> {
    return this.wrapRequest(
      of({ success: true, fileName: file.name }).pipe(delay(2000)),
      true,
      "Failed to upload profile picture"
    );
  }

  override createOrUpdateEducation(data: Education[]): Observable<Education[]> {
    return this.wrapRequest(
      of(data).pipe(delay(1500)),
      true
    );
  }

  override getCurrentUserEducation(): Observable<Education[]> {
    return this.wrapRequest(
      of([
        {
          degree: "Bachelor of Technology",
          instituteName: "Stanford University",
          startYear: 2016,
          endYear: 2020,
          projects: []
        }
      ]).pipe(delay(1000)),
      false
    );
  }

  override getCurrentWorkExperience(): Observable<Experience[]> {
    return this.wrapRequest(
      of([
        {
          companyName: "Tech Solutions Inc",
          jobTitle: "Software Engineer",
          startYear: 2020,
          endYear: 2023,
          keyResponsibilities: "Worked on full-stack development",
          projects: []
        }
      ]).pipe(delay(1000)),
      false
    );
  }

  override deleteEducation(uniqueId: string): Observable<void> {
    return this.wrapRequest(
      of(undefined).pipe(delay(1000)),
      false,
      "Failed to delete education"
    );
  }

  override deleteExperience(uniqueId: string): Observable<void> {
    return this.wrapRequest(
      of(undefined).pipe(delay(1000)),
      false,
      "Failed to delete experience"
    );
  }

  override createWorkExperience(data: Experience[]): Observable<Experience[]> {
    return this.wrapRequest(
      of(data).pipe(delay(1500)),
      false,
      "Failed to create work experience"
    );
  }

  override updateWorkExperience(
    uniqueId: string,
    data: Experience
  ): Observable<Experience> {
    return this.wrapRequest(
      of(data).pipe(delay(1500)),
      false,
      "Failed to update work experience"
    );
  }

  override getExperiencedProjects(): Observable<any> {
    return this.wrapRequest(
      of([]).pipe(delay(1000)),
      false,
      "Failed to fetch projects"
    );
  }

  override getExperienceRange(): Observable<any> {
    return this.wrapRequest(
      of({ min: 0, max: 20 }).pipe(delay(1000)),
      false,
      "Failed to fetch experience range"
    );
  }
}
